import { useCallback, useEffect, useMemo, useState } from 'react';
import { employees, departments, designations, account } from '../api/client';
import { notify } from '../notify';

const EMPLOYMENT_TYPES = ['FULL_TIME', 'PART_TIME', 'CONTRACT', 'INTERN', 'CONSULTANT'];
const STATUSES = ['PROBATION', 'ACTIVE', 'NOTICE_PERIOD', 'SUSPENDED', 'EXITED'];
const GENDERS = ['MALE', 'FEMALE', 'OTHER', 'UNDISCLOSED'];

const blankForm = {
  employeeCode: '',
  firstName: '',
  lastName: '',
  workEmail: '',
  personalEmail: '',
  phone: '',
  dateOfBirth: '',
  gender: '',
  address: '',
  departmentId: '',
  designationId: '',
  managerId: '',
  employmentType: 'FULL_TIME',
  status: 'PROBATION',
  dateOfJoining: '',
  workLocation: '',
};

/** Strips empty strings so the backend sees absent fields as null, not "". */
function toPayload(form) {
  const out = {};
  Object.entries(form).forEach(([k, v]) => {
    if (v === '' || v === null) return;
    out[k] = ['departmentId', 'designationId', 'managerId'].includes(k) ? Number(v) : v;
  });
  return out;
}

function toForm(emp) {
  return {
    ...blankForm,
    ...emp,
    lastName: emp.lastName || '',
    personalEmail: emp.personalEmail || '',
    phone: emp.phone || '',
    dateOfBirth: emp.dateOfBirth || '',
    gender: emp.gender || '',
    address: emp.address || '',
    workLocation: emp.workLocation || '',
    dateOfJoining: emp.dateOfJoining || '',
    departmentId: emp.department ? emp.department.id : '',
    designationId: emp.designation ? emp.designation.id : '',
    managerId: emp.managerId || '',
  };
}

export default function Employees() {
  const [rows, setRows] = useState([]);
  const [depts, setDepts] = useState([]);
  const [desigs, setDesigs] = useState([]);
  const [headcount, setHeadcount] = useState(null);
  const [includeExited, setIncludeExited] = useState(false);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState(null); // null | 'new' | employee id
  const [form, setForm] = useState(blankForm);
  const [saving, setSaving] = useState(false);

  const [logins, setLogins] = useState({});         // employeeId -> account status
  const [roleChoice, setRoleChoice] = useState({}); // employeeId -> USER | ADMIN
  const [issued, setIssued] = useState(null);       // one-time credentials to show
  const [revealPw, setRevealPw] = useState(false);
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, d, g, hc, acc] = await Promise.all([
        employees.list(includeExited),
        departments.list(),
        designations.list(),
        employees.headcount(),
        account.list().catch(() => []),
      ]);
      const byEmp = {};
      (acc || []).forEach((a) => { byEmp[a.employeeId] = a; });
      setLogins(byEmp);
      setRows(list || []);
      setDepts(d || []);
      setDesigs(g || []);
      setHeadcount(hc);
    } catch (err) {
      notify.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [includeExited]);

  useEffect(() => {
    load();
  }, [load]);

  const byId = useMemo(() => {
    const m = {};
    rows.forEach((r) => (m[r.id] = r));
    return m;
  }, [rows]);

  async function startNew() {
    setForm(blankForm);
    setEditing('new');
    // Suggestion only - the server allocates the real code on save, so two
    // people with the form open at once cannot save the same one.
    try {
      const { employeeCode } = await employees.nextCode();
      setForm((f) => ({ ...f, employeeCode }));
    } catch {
      // Leaving it blank is fine: the server generates one when it is empty.
    }
  }

  function startEdit(emp) {
    setForm(toForm(emp));
    setEditing(emp.id);
  }

  function cancel() {
    setEditing(null);
    setForm(blankForm);
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = toPayload(form);
      if (editing === 'new') {
        await employees.create(payload);
        notify.success(`Created ${payload.employeeCode}`);
      } else {
        await employees.update(editing, payload);
        notify.success(`Updated ${payload.employeeCode || ''}`.trim());
      }
      cancel();
      await load();
    } catch (err) {
      notify.error(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function markExit(emp) {
    const date = window.prompt(
      `Exit date for ${emp.fullName} (YYYY-MM-DD). Leave blank for today.`,
      ''
    );
    if (date === null) return;
    try {
      await employees.exit(emp.id, date.trim() || undefined);
      notify.success(`${emp.employeeCode} marked as exited`);
      await load();
    } catch (err) {
      notify.error(err.message);
    }
  }

  async function grantAccess(emp) {
    const a = logins[emp.id] || {};
    setActing(emp.id);
    setIssued(null);
    try {
      if (a.existingAccount) {
        const res = await account.linkExisting(emp.id);
        notify.success(`${emp.employeeCode} linked to ${res.username}. They sign in with their existing password.`
          + (res.warning ? ` ⚠ ${res.warning}` : ''));
      } else {
        const res = await account.createLogin(emp.id, roleChoice[emp.id] || 'USER');
        setRevealPw(false);
        setIssued({ ...res, name: emp.fullName, kind: 'created' });
      }
      await load();
    } catch (e) {
      notify.error(e.message);
    } finally {
      setActing(null);
    }
  }

  async function resetPassword(emp) {
    if (!window.confirm(`Reset the password for ${emp.fullName}? Their current password will stop working.`)) return;
    setActing(emp.id);
    setIssued(null);
    try {
      const res = await account.resetPassword(emp.id);
      setRevealPw(false);
      setIssued({ ...res, name: emp.fullName, kind: 'reset' });
    } catch (e) {
      notify.error(e.message);
    } finally {
      setActing(null);
    }
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div>
      <div className="row-between">
        <div>
          <h2>Employees</h2>
          <p className="muted">
            {headcount !== null && <>On roll: <strong>{headcount}</strong> · </>}
            Showing {rows.length} {includeExited ? '(including exited)' : '(excluding exited)'}
          </p>
        </div>
        <div className="actions">
          <label className="check">
            <input
              type="checkbox"
              checked={includeExited}
              onChange={(e) => setIncludeExited(e.target.checked)}
            />
            Include exited
          </label>
          <button className="btn btn-primary" onClick={startNew}>+ Add employee</button>
        </div>
      </div>

      {issued && (
        <div className="card cred-card">
          <div className="row-between" style={{ marginBottom: 8 }}>
            <h3 style={{ margin: 0 }}>
              {issued.kind === 'reset' ? 'Password reset' : 'Login created'} · {issued.employeeCode} {issued.name}
            </h3>
            <button className="btn btn-sm" onClick={() => setIssued(null)}>Done</button>
          </div>
          {issued.emailSent ? (
            <div className="alert alert-ok" style={{ cursor: 'default' }}>
              ✓ Sign-in details emailed to <strong>{issued.emailedTo}</strong>. Nothing more to do.
            </div>
          ) : (
            <div className="alert alert-error" style={{ cursor: 'default' }}>
              ✗ Email to {issued.emailedTo || 'the employee'} could not be sent
              {issued.emailError ? ` (${issued.emailError})` : ''}. Share the password below with them directly.
            </div>
          )}
          <div className="cred-grid">
            <span className="muted">Sign-in page</span><span className="mono">{window.location.origin}</span>
            <span className="muted">Email</span><span className="mono">{issued.username}</span>
            <span className="muted">Temporary password</span>
            <span>
              {issued.emailSent && !revealPw ? (
                <>
                  <span className="mono cred-pass">••••••••••</span>{' '}
                  <button className="btn btn-sm" onClick={() => setRevealPw(true)}>Show</button>
                </>
              ) : (
                <>
                  <span className="mono cred-pass">{issued.temporaryPassword}</span>{' '}
                  <button className="btn btn-sm" onClick={() => {
                    navigator.clipboard && navigator.clipboard.writeText(
                      `Cloudmyle ERM\nSign in: ${window.location.origin}\nEmail: ${issued.username}\nTemporary password: ${issued.temporaryPassword}\nYou will be asked to choose your own password when you sign in.`);
                    notify.success('Copied - send it to them directly, not in a group chat.');
                  }}>Copy details</button>
                </>
              )}
            </span>
          </div>
          <p className="small muted" style={{ marginTop: 10 }}>
            Shown once only — it cannot be displayed again. They will be asked to choose their own
            password when they sign in, and this temporary one then stops working.
          </p>
        </div>
      )}

      {editing !== null && (
        <form className="card form-grid" onSubmit={save}>
          <h3 className="span-all">{editing === 'new' ? 'New employee' : `Edit #${editing}`}</h3>

          <label className="field">
            <span>Employee code</span>
            <input value={form.employeeCode} onChange={set('employeeCode')}
                   placeholder="auto" />
            {editing === 'new' && (
              <span className="small muted">Auto-generated. Edit to override.</span>
            )}
          </label>
          <label className="field">
            <span>First name *</span>
            <input value={form.firstName} onChange={set('firstName')} required />
          </label>
          <label className="field">
            <span>Last name</span>
            <input value={form.lastName} onChange={set('lastName')} />
          </label>
          <label className="field">
            <span>Work email *</span>
            <input type="email" value={form.workEmail} onChange={set('workEmail')} required />
          </label>
          <label className="field">
            <span>Personal email</span>
            <input type="email" value={form.personalEmail} onChange={set('personalEmail')} />
          </label>
          <label className="field">
            <span>Phone</span>
            <input value={form.phone} onChange={set('phone')} />
          </label>
          <label className="field">
            <span>Department</span>
            <select value={form.departmentId} onChange={set('departmentId')}>
              <option value="">—</option>
              {depts.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Designation</span>
            <select value={form.designationId} onChange={set('designationId')}>
              <option value="">—</option>
              {desigs.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Reports to</span>
            <select value={form.managerId} onChange={set('managerId')}>
              <option value="">—</option>
              {rows
                .filter((r) => r.id !== editing && r.onRoll)
                .map((r) => (
                  <option key={r.id} value={r.id}>{r.employeeCode} · {r.fullName}</option>
                ))}
            </select>
          </label>
          <label className="field">
            <span>Employment type</span>
            <select value={form.employmentType} onChange={set('employmentType')}>
              {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Status</span>
            <select value={form.status} onChange={set('status')}>
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Gender</span>
            <select value={form.gender} onChange={set('gender')}>
              <option value="">—</option>
              {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Date of joining</span>
            <input type="date" value={form.dateOfJoining} onChange={set('dateOfJoining')} />
          </label>
          <label className="field">
            <span>Date of birth</span>
            <input type="date" value={form.dateOfBirth} onChange={set('dateOfBirth')} />
          </label>
          <label className="field">
            <span>Work location</span>
            <input value={form.workLocation} onChange={set('workLocation')} />
          </label>
          <label className="field span-all">
            <span>Address</span>
            <textarea rows={2} value={form.address} onChange={set('address')} />
          </label>

          <div className="span-all actions">
            <button className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn" onClick={cancel}>Cancel</button>
          </div>
        </form>
      )}

      {loading ? (
        <p className="muted">Loading…</p>
      ) : rows.length === 0 ? (
        <div className="card empty">
          <p><strong>No employees yet.</strong></p>
          <p className="muted">Add a department and designation first, then create an employee.</p>
        </div>
      ) : (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Code</th><th>Name</th><th>Work email</th><th>Department</th>
                <th>Designation</th><th>Reports to</th><th>Type</th><th>Status</th><th>Portal login</th><th></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className={r.onRoll ? '' : 'exited'}>
                  <td className="mono nowrap">{r.employeeCode}</td>
                  <td>{r.fullName}</td>
                  <td className="mono small">{r.workEmail}</td>
                  <td>{r.department ? r.department.name : '—'}</td>
                  <td>{r.designation ? r.designation.title : '—'}</td>
                  <td>
                    {r.managerId
                      ? (byId[r.managerId] ? byId[r.managerId].employeeCode : `#${r.managerId}`)
                      : '—'}
                  </td>
                  <td className="small nowrap">{r.employmentType.replace('_', ' ')}</td>
                  <td>
                    <span className={`pill pill-${r.status.toLowerCase()}`}>
                      {r.status.replace('_', ' ')}
                    </span>
                    {r.dateOfExit && <div className="small muted">{r.dateOfExit}</div>}
                  </td>
                  <td className="login-cell">
                    {(() => {
                      const a = logins[r.id] || {};
                      if (a.userId) {
                        const admin = (a.roles || []).some((x) => x === 'ADMIN' || x === 'AUTHORITY');
                        return (
                          <>
                            <div className="mono small">{a.username}</div>
                            <span className={`pill ${admin ? 'pill-submitted' : 'pill-active'}`}>
                              {admin ? 'HR / ADMIN' : 'EMPLOYEE'}
                            </span>{' '}
                            {a.mustChangePassword && (
                              <span className="pill pill-probation" title="Has not signed in and set their own password yet">
                                NOT ACTIVATED
                              </span>
                            )}{' '}
                            <button className="linklike small" disabled={acting === r.id}
                                    onClick={() => resetPassword(r)}>Reset password</button>
                          </>
                        );
                      }
                      if (!r.onRoll) return <span className="small muted">—</span>;
                      if (a.existingAccount) {
                        return (
                          <>
                            <div className="small muted">Account exists for this email</div>
                            <button className="btn btn-sm btn-primary" disabled={acting === r.id}
                                    onClick={() => grantAccess(r)}>Link account</button>
                          </>
                        );
                      }
                      return (
                        <div className="nowrap">
                          <select className="role-select" value={roleChoice[r.id] || 'USER'}
                                  onChange={(e) => setRoleChoice({ ...roleChoice, [r.id]: e.target.value })}>
                            <option value="USER">Employee</option>
                            <option value="ADMIN">HR / Admin</option>
                          </select>{' '}
                          <button className="btn btn-sm btn-primary" disabled={acting === r.id}
                                  onClick={() => grantAccess(r)}>Create login</button>
                        </div>
                      );
                    })()}
                  </td>
                  <td className="nowrap">
                    <button className="btn btn-sm" onClick={() => startEdit(r)}>Edit</button>
                    {r.onRoll && (
                      <button className="btn btn-sm btn-danger" onClick={() => markExit(r)}>Exit</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
